import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton,
  Alert,
  Chip,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import { usePrompt } from '../../lib/hooks/UsePrompt';
import { usePersona } from '../../lib/hooks/UsePersona';
import { PromptComponent, PROMPT_COMPONENT_TYPES } from '@common/domain/types';
import { Persona } from '../../lib/api/Persona';
import { useUserInfoContext } from '../../lib/contexts/UserInfoContext';

export default function PromptComponentManager() {
  const { getAllComponents, updateComponent, createComponent, deleteComponent } = usePrompt();
  const { getPersonas } = usePersona();
  const { userInfo } = useUserInfoContext();
  const [components, setComponents] = useState<PromptComponent[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog state
  const [open, setOpen] = useState(false);
  const [editingComponent, setEditingComponent] = useState<PromptComponent | null>(null);
  const [formData, setFormData] = useState<PromptComponent>({
    id: '',
    type: '',
    content: '',
    isActive: true
  });

  // Filter state
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [compData, personaData] = await Promise.all([
        getAllComponents(),
        getPersonas()
      ]);
      setComponents(compData);
      setPersonas(personaData || []);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userInfo) return;

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  const types = useMemo(() => {
    // Combine built-in types with any existing types in the data
    const distinctTypes = new Set([...PROMPT_COMPONENT_TYPES, ...components.map(c => c.type)]);
    return Array.from(distinctTypes).sort();
  }, [components]);

  const filteredComponents = useMemo(() => {
    return components.filter(c => {
      const matchesType = typeFilter === 'all' || c.type === typeFilter;
      const matchesSearch = c.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           c.content.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesType && matchesSearch;
    });
  }, [components, typeFilter, searchQuery]);

  const handleOpenDialog = (component: PromptComponent | null = null) => {
    if (component) {
      setEditingComponent(component);
      setFormData({ ...component });
    } else {
      setEditingComponent(null);
      setFormData({
        id: '',
        type: '',
        content: '',
        isActive: true
      });
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingComponent(null);
  };

  const getComponentUsage = (type: string, id: string) => {
    return personas.filter(p => {
      if (!p.promptConfig || !Array.isArray(p.promptConfig)) return false;
      return p.promptConfig.some((item: any) => {
        if (item.id === id && item.type === type) return true;
        if (item.mapping) {
          return Object.values(item.mapping).some((m: any) => m.id === id && m.type === type);
        }
        return false;
      });
    });
  };

  const handleSave = async () => {
    try {
      if (editingComponent) {
        await updateComponent(editingComponent.type, editingComponent.id, formData);
        setComponents(prev => prev.map(c =>
          (c.id === editingComponent.id && c.type === editingComponent.type) ? formData : c
        ));
      } else {
        await createComponent(formData);
        setComponents(prev => [...prev, formData]);
      }
      handleCloseDialog();
    } catch (err: any) {
      setError(`Failed to save component: ${err.message || 'Unknown error'}`);
      console.error(err);
    }
  };

  const handleDelete = async (type: string, id: string) => {
    if (!window.confirm(`Are you sure you want to delete component ${type}:${id}?`)) {
      return;
    }

    try {
      await deleteComponent(type, id);
      setComponents(prev => prev.filter(c => !(c.type === type && c.id === id)));
    } catch (err) {
      setError('Failed to delete component');
      console.error(err);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Prompt Components</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          label="Search ID or Content"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 300 }}
        />
        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Type Filter</InputLabel>
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            label="Type Filter"
          >
            <MenuItem value="all">All Types</MenuItem>
            {types.map(t => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Create New
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Preview</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} align="center">Loading...</TableCell></TableRow>
            ) : filteredComponents.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center">No components found</TableCell></TableRow>
            ) : filteredComponents.map((component) => (
              <TableRow key={`${component.type}:${component.id}`}>
                <TableCell><Chip label={component.type} size="small" variant="outlined" /></TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {component.id}
                    {getComponentUsage(component.type, component.id).length > 0 && (
                      <Tooltip title={`Used by: ${getComponentUsage(component.type, component.id).map(p => p.name).join(', ')}`}>
                        <InfoIcon color="info" sx={{ fontSize: 16 }} />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={component.isActive ? "Active" : "Inactive"}
                    color={component.isActive ? "success" : "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {component.content}
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpenDialog(component)} color="primary" size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(component.type, component.id)} color="error" size="small">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingComponent ? 'Edit Component' : 'Create Component'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {editingComponent && getComponentUsage(editingComponent.type, editingComponent.id).length > 0 && (
              <Alert severity="warning" icon={<WarningIcon />}>
                This component is used by <strong>{getComponentUsage(editingComponent.type, editingComponent.id).length}</strong> persona(s):
                {' '}{getComponentUsage(editingComponent.type, editingComponent.id).map(p => p.name).join(', ')}.
                Changes will affect all of them.
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth sx={{ flex: 1 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Type"
                  disabled={!!editingComponent}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                >
                  {types.map(t => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="ID"
                required
                disabled={!!editingComponent}
                value={formData.id}
                onChange={(e) => setFormData(prev => ({ ...prev, id: e.target.value }))}
                sx={{ flex: 1 }}
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                />
              }
              label="Active"
            />
            <TextField
              label="Content"
              required
              multiline
              rows={12}
              fullWidth
              value={formData.content}
              onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Support Handlebars-style parameters like {{parameter}}"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={!formData.id || !formData.type || !formData.content}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
